/**
 * Fetches the stored MN judge assessment when the questionnaire page loads
 * so the toolbar can show decision / confidence before opening the modal.
 *
 * Uses the same GET as Prediction Summary; only surfaces the summary badges.
 *
 * @module pharmaQuestionaire/hooks/useMnJudgeAssessmentPreview
 */
import { useEffect, useState } from "react";
import { fetchMnJudgeAssessment } from "../../../api/getCalls/fetchMnJudgeAssessment";
import { mapAssessmentToSummary } from "../../../components/modals/predictionSummaryModal/mapAssessmentToSummary";
import { EvaluationDecision } from "../../../components/modals/predictionSummaryModal/types";
import { logError } from "../../../utils/customLogger";

/** Decision and confidence shown next to Prediction Summary on the page. */
export interface MnJudgeAssessmentPreview {
  /** Normalized evaluation decision, or null when unavailable. */
  decision: EvaluationDecision | null;
  /** Confidence percent (0–100), or null when unavailable. */
  confidence: number | null;
  /** True while the assessment request is in flight. */
  loading: boolean;
}

/**
 * Loads the latest MN judge verdict for a case identifier on mount / id change.
 *
 * @param identifier - PA case identifier used by GET mn-judge/assessment
 * @returns Preview fields for toolbar badges
 */
export const useMnJudgeAssessmentPreview = (
  identifier: string,
): MnJudgeAssessmentPreview => {
  const [decision, setDecision] = useState<EvaluationDecision | null>(null);
  const [confidence, setConfidence] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    /**
     * Fetches and maps the latest assessment into decision / confidence.
     */
    const loadPreview = async () => {
      if (!identifier) {
        setDecision(null);
        setConfidence(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setDecision(null);
      setConfidence(null);

      try {
        const response = await fetchMnJudgeAssessment(identifier);
        if (!isMounted) return;

        if (!response.latest) {
          return;
        }

        const summary = mapAssessmentToSummary(response.latest);
        setDecision(summary.decision);
        setConfidence(summary.confidence);
      } catch (error) {
        logError(error as Error, "Error loading MN judge assessment preview");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isMounted = false;
    };
  }, [identifier]);

  return { decision, confidence, loading };
};
